<template>
        <div class="text-element-container" :class="{ active, locked }" :style="!inGroup ? containerStyle : ''">
                <div @mousedown.stop="handleMouseDown" @touchstart.stop="handleTouchStart" class="text-content" ref="textContent" :contenteditable="!locked"
                        @input="handleInput" @blur="handleBlur" @focus="handleFocus"
                        :style="[textStyle, applyFiltersToDom(item.filters)]"></div>

                <!-- Панель управления (скрыта на мобильных - используется общая мобильная панель) -->
                <div v-if="active && showControls && !isMobile" class="item-controls">
                        <div class="images-buttons" :class="{ 'toolbar-below': toolbarBelow }">
                                <img src="@/assets/images/content/key.svg" @click.stop="toggleLock" :class="{ active: locked }" />
                                <img src="@/assets/images/content/copy.svg" @click.stop="copyItem" />

                                <div class="indexIcon" :class="{ 'is-active': activeControlButton === 'opacity' }">
                                        <img src="@/assets/images/content/index.svg" @click.stop="handleControlButtonClick('opacity')" />
                                        <div class="index-block-input">
                                                <div class="index-inputs-block">
                                                        <span class="index-text">{{ $t('legacy.Прозрачность') }}</span>
                                                        <input type="range" class="index-input" :value="Math.round(opacity * 100)" @input="updateOpacity($event)"
                                                                min="0" max="100" step="1" :style="sliderStyle" />
                                                        <span class="index-number">{{ Math.round(opacity * 100) }}%</span>
                                                </div>
                                        </div>
                                </div>

                                <div class="layers-control" :class="{ 'is-active': activeControlButton === 'layers' }">
                                        <img src="@/assets/images/content/layers.svg" @click.stop="handleControlButtonClick('layers')" />
                                        <div class="layers-menu">
                                                <div @click.stop="moveLayer('front')">
                                                        <img src="@/assets/images/content/up-full.svg" /> {{ $t('legacy.На передний план') }}
                                                </div>
                                                <div @click.stop="moveLayer('up')">
                                                        <img src="@/assets/images/content/up.svg" /> {{ $t('editor.moveUp') }}
                                                </div>
                                                <div @click.stop="moveLayer('down')">
                                                        <img src="@/assets/images/content/down.svg" /> {{ $t('editor.moveDown') }}
                                                </div>
                                                <div @click.stop="moveLayer('back')">
                                                        <img src="@/assets/images/content/down-full.svg" /> {{ $t('legacy.На задний план') }}
                                                </div>
                                        </div>
                                </div>

                                <div class="color-picker-wrapper" :class="{ 'is-active': activeControlButton === 'color' }">
                                        <div class="color-preview-btn" @click.stop="handleControlButtonClick('color')" :style="{ backgroundColor: color }"></div>
                                        <div class="color-picker-dropdown">
                                                <AppColorPicker
                                                        v-model="color"
                                                        @update:model-value="updateColorFromPicker"
                                                />
                                        </div>
                                </div>

                                <img src="@/assets/images/content/shadow.svg" @click.stop="toggleShadow" class="shadow-icon" :class="{ active: textShadow }" />
                                <img src="@/assets/images/content/rotate.svg" @click.stop="openRotatePicker" class="rotate-icon" />
                                <img src="@/assets/images/content/delete-element.svg" @click.stop="deleteItem" class="delete-icon" />
                        </div>

                        <AppClockRotatePicker v-if="showRotatePicker" :initialAngle="rotate" @update="handleRotateUpdate"
                                @close="closeRotatePicker" />
                </div>

                <!-- Ресайз-хендлы -->
                <template v-if="active && showControls && !isLocked">
                        <div v-for="handle in resizeHandles" :key="handle" class="resize-handle" :class="`resize-handle-${handle}`"
                                @mousedown.stop.prevent="startResize($event, handle)"
                                @touchstart.stop.prevent="startResizeTouch($event, handle)"></div>
                </template>
        </div>
</template>

<script>
import { computed, nextTick, onMounted, ref, watch, onUnmounted } from "vue"
import AppClockRotatePicker from './AppClockRotatePicker.vue'
import AppColorPicker from './AppColorPicker.vue'
import { t } from '@/i18n'

export default {
        name: "TextElement",
        components: {
                AppClockRotatePicker,
                AppColorPicker
        },
        props: {
                item: {
                        type: Object,
                        required: true,
                },
                active: Boolean,
                showControls: Boolean,
                locked: Boolean,
                applyFiltersToDom: {
                        type: Function,
                        default: () => {}
                },
                inGroup: Boolean,
                isMobile: {
                        type: Boolean,
                        default: false
                },
                scale: {
                        type: Number,
                        default: 1
                }
        },

        setup(props, { emit }) {
                const id = props.item.id || Math.random().toString(36).slice(2)
                const x = ref(props.item.x || 100)
                const y = ref(props.item.y || 100)
                const DEFAULT_WIDTH = 150
                const DEFAULT_HEIGHT = 40
                const width = ref(props.item.width || DEFAULT_WIDTH)
                const height = ref(props.item.height || DEFAULT_HEIGHT)
                const rotate = ref(props.item.rotate || 0)
                const zIndex = ref(props.item.zIndex || 1)
                const fontName = ref(props.item.fontName || "Arial")
                const fontCategory = ref(props.item.fontCategory || "sans-serif")
                const text = ref(props.item.text || "")
                const color = ref(props.item.color || "#000000")
                const opacity = ref(props.item.opacity ?? 1)
                const textShadow = ref(props.item.textShadow || false)
                const isLocked = ref(props.item.locked || false)
                const showRotatePicker = ref(false)
                const activeControlButton = ref(null)
                // Store fontSize as a separate property - legacy fallback calculates from height
                const fontSize = ref(props.item.fontSize || Math.max(16, (props.item.height || 40) * 0.7))

                const textContent = ref(null)
                const isDragging = ref(false)
                const isResizing = ref(false)
                const startPos = ref({ x: 0, y: 0 })
                const startSize = ref({ width: 0, height: 0 })
                const startCoords = ref({ x: 0, y: 0 })
                const startPosition = ref({ x: 0, y: 0 })
                const resizeDirection = ref(null)
                const startFontSize = ref(0)

                const MIN_SIZE = 30

                const resizeHandles = [
                        "right",
                        "left",
                        "bottom",
                        "top",
                        "top-right",
                        "top-left",
                        "bottom-left",
                        "bottom-right",
                ]

                const containerStyle = computed(() => {
                        const s = props.scale || 1
                        return {
                                position: "absolute",
                                top: (y.value * s) + "px",
                                left: (x.value * s) + "px",
                                width: (width.value * s) + "px",
                                height: (height.value * s) + "px",
                                zIndex: zIndex.value,
                                cursor: isDragging.value ? "grabbing" : isLocked.value ? "default" : "grab",
                                userSelect: isDragging.value ? "none" : "auto",
                                transformOrigin: "center",
                                transform: `rotate(${rotate.value}deg)`,
                                pointerEvents: 'auto'
                        }
                })

                const textStyle = computed(() => {
                        const s = props.scale || 1
                        // Use stored fontSize scaled by current scale factor
                        const scaledFontSize = fontSize.value * s

                        return {
                                fontFamily: fontName.value ? `'${fontName.value}', ${fontCategory.value}` : "Arial",
                                fontSize: scaledFontSize + "px",
                                color: color.value,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                outline: "none",
                                wordBreak: "break-word",
                                overflow: "hidden",
                                opacity: opacity.value,
                                whiteSpace: "pre-wrap",
                                overflowWrap: "break-word",
                                lineHeight: "1.2",
                                userSelect: isLocked.value ? "none" : "text",
                                textShadow: textShadow.value ? '2px 2px 4px rgba(0, 0, 0, 0.5)' : 'none',
                        }
                })

                const sliderStyle = computed(() => ({
                        "--fill-position": `${Math.round(opacity.value * 100)}%`,
                }))

                const toolbarBelow = computed(() => {
                        return y.value < 60
                })

                const handleControlButtonClick = (buttonType) => {
                        if (activeControlButton.value === buttonType) {
                                activeControlButton.value = null;
                        } else {
                                activeControlButton.value = buttonType;
                        }
                }

                const closeAllMenus = () => {
                        activeControlButton.value = null;
                }

                const openRotatePicker = () => {
                        showRotatePicker.value = true
                        closeAllMenus()
                }

                const closeRotatePicker = () => {
                        showRotatePicker.value = false
                        emit("close-rotate-picker", id)
                }

                const handleRotateUpdate = (newAngle) => {
                        rotate.value = newAngle
                        nextTick(() => {
                                adjustSizeForRotation()
                        })
                        emit("update:rotate", rotate.value)
                }

                const adjustSizeForRotation = () => {
                        if (!textContent.value) return

                        const angle = rotate.value % 360

                        if (angle === 90 || angle === 270) {
                                const el = textContent.value

                                const originalWhiteSpace = el.style.whiteSpace
                                const originalWidth = el.style.width
                                const originalHeight = el.style.height

                                el.style.whiteSpace = "pre-wrap"
                                el.style.width = "auto"
                                el.style.height = "auto"

                                const contentWidth = el.scrollWidth
                                const contentHeight = el.scrollHeight
                                const padding = 16

                                const targetWidth = Math.max(MIN_SIZE, contentHeight + padding)
                                const targetHeight = Math.max(MIN_SIZE, contentWidth + padding)

                                if (width.value < targetWidth) width.value = targetWidth
                                if (height.value < targetHeight) height.value = targetHeight

                                el.style.whiteSpace = originalWhiteSpace
                                el.style.width = originalWidth
                                el.style.height = originalHeight
                        }
                }

                const adjustWidthToContent = () => {
                        if (!textContent.value) return
                        
                        const currentText = text.value.trim()
                        
                        // Если текст пустой или placeholder, используем начальный размер
                        if (!currentText || currentText === t('legacy.Введите текст')) {
                                width.value = DEFAULT_WIDTH
                                height.value = DEFAULT_HEIGHT
                                emit("update-size-position", id, {
                                        x: x.value,
                                        y: y.value,
                                        width: width.value,
                                        height: height.value,
                                        fontSize: fontSize.value
                                })
                                return
                        }
                        
                        // Измеряем реальный размер текста используя сам элемент
                        const el = textContent.value
                        
                        // Сохраняем текущие стили
                        const originalWidth = el.style.width
                        const originalHeight = el.style.height
                        const originalWhiteSpace = el.style.whiteSpace
                        
                        // Временно меняем для измерения
                        el.style.width = 'auto'
                        el.style.height = 'auto'
                        el.style.whiteSpace = 'pre-wrap'
                        
                        // Измеряем
                        const textWidth = el.scrollWidth
                        const textHeight = el.scrollHeight
                        
                        // Восстанавливаем стили
                        el.style.width = originalWidth
                        el.style.height = originalHeight
                        el.style.whiteSpace = originalWhiteSpace
                        
                        const s = props.scale || 1
                        
                        // Минимальный отступ от текста до рамки
                        const paddingX = 20
                        const paddingY = 10
                        
                        // Устанавливаем размеры с небольшим отступом
                        const newWidth = Math.max(MIN_SIZE, (textWidth / s) + paddingX)
                        const newHeight = Math.max(MIN_SIZE, (textHeight / s) + paddingY)
                        
                        width.value = newWidth
                        height.value = newHeight
                        
                        // Уведомляем о изменении размера
                        emit("update-size-position", id, {
                                x: x.value,
                                y: y.value,
                                width: width.value,
                                height: height.value,
                                fontSize: fontSize.value
                        })
                }

                const handleMouseDown = (e) => {
                        if (isLocked.value || props.inGroup) return

                        closeAllMenus()
                        emit("select-item", id, e)

                        isDragging.value = true
                        const s = props.scale || 1
                        startPos.value = {
                                x: e.clientX - x.value * s,
                                y: e.clientY - y.value * s,
                        }

                        document.addEventListener("mousemove", handleMouseMove)
                        document.addEventListener("mouseup", handleMouseUp)
                }

                const handleMouseMove = (e) => {
                        if (!isDragging.value || isLocked.value || isResizing.value) return

                        const s = props.scale || 1
                        x.value = (e.clientX - startPos.value.x) / s
                        y.value = (e.clientY - startPos.value.y) / s
                }

                const handleMouseUp = () => {
                        if (isDragging.value) {
                                isDragging.value = false
                                emit("update-position", id, x.value, y.value)
                        }
                        document.removeEventListener("mousemove", handleMouseMove)
                        document.removeEventListener("mouseup", handleMouseUp)
                }

                const handleTouchStart = (e) => {
                        if (isLocked.value || props.inGroup) return
                        if (!e.touches || e.touches.length === 0) return

                        closeAllMenus()
                        
                        const wasAlreadyActive = props.active
                        emit("select-item", id, e)
                        
                        // На мобильном: если элемент не был активен, только активируем без перетаскивания
                        if (props.isMobile && !wasAlreadyActive) {
                                return
                        }

                        const touch = e.touches[0]
                        const touchStartX = touch.clientX
                        const touchStartY = touch.clientY
                        const touchStartTime = Date.now()
                        let hasMoved = false
                        const MOVE_THRESHOLD = 10 // pixels
                        const TAP_THRESHOLD = 300 // ms

                        const s = props.scale || 1
                        startPos.value = {
                                x: touch.clientX - x.value * s,
                                y: touch.clientY - y.value * s,
                        }

                        const handleTouchMove = (moveEvent) => {
                                if (isLocked.value || isResizing.value) return
                                if (!moveEvent.touches || moveEvent.touches.length === 0) return

                                const moveTouch = moveEvent.touches[0]
                                const deltaX = Math.abs(moveTouch.clientX - touchStartX)
                                const deltaY = Math.abs(moveTouch.clientY - touchStartY)
                                
                                // Определяем, было ли реальное движение
                                if (deltaX > MOVE_THRESHOLD || deltaY > MOVE_THRESHOLD) {
                                        hasMoved = true
                                        isDragging.value = true
                                        moveEvent.preventDefault() // Предотвращаем скролл только при перетаскивании
                                        
                                        x.value = (moveTouch.clientX - startPos.value.x) / s
                                        y.value = (moveTouch.clientY - startPos.value.y) / s
                                }
                        }

                        const handleTouchEnd = () => {
                                const touchDuration = Date.now() - touchStartTime
                                
                                if (isDragging.value) {
                                        isDragging.value = false
                                        emit("update-position", id, x.value, y.value)
                                }
                                
                                // Если это был тап (короткое касание без движения) на уже активном элементе - фокусируем для редактирования
                                if (!hasMoved && touchDuration < TAP_THRESHOLD && wasAlreadyActive) {
                                        nextTick(() => {
                                                if (textContent.value) {
                                                        textContent.value.focus()
                                                }
                                        })
                                }
                                
                                document.removeEventListener("touchmove", handleTouchMove)
                                document.removeEventListener("touchend", handleTouchEnd)
                        }

                        document.addEventListener("touchmove", handleTouchMove, { passive: false })
                        document.addEventListener("touchend", handleTouchEnd)
                }

                const startResize = (event, direction) => {
                        if (isLocked.value) return
                        event.preventDefault()
                        event.stopPropagation()

                        closeAllMenus()
                        isResizing.value = true
                        resizeDirection.value = direction
                        startCoords.value = { x: event.clientX, y: event.clientY }
                        startSize.value = { width: width.value, height: height.value }
                        startPosition.value = { x: x.value, y: y.value }
                        startFontSize.value = fontSize.value
                        const aspectRatio = width.value / height.value
                        const s = props.scale || 1

                        const handleResizeMove = (e) => {
                                if (!isResizing.value) return

                                const deltaX = (e.clientX - startCoords.value.x) / s
                                const deltaY = (e.clientY - startCoords.value.y) / s

                                let newWidth = startSize.value.width
                                let newHeight = startSize.value.height
                                let newX = startPosition.value.x
                                let newY = startPosition.value.y

                                switch (resizeDirection.value) {
                                        case "right":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width + deltaX)
                                                break
                                        case "left":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width - deltaX)
                                                newX = startPosition.value.x + deltaX
                                                break
                                        case "bottom":
                                                newHeight = Math.max(MIN_SIZE, startSize.value.height + deltaY)
                                                break
                                        case "top":
                                                newHeight = Math.max(MIN_SIZE, startSize.value.height - deltaY)
                                                newY = startPosition.value.y + deltaY
                                                break
                                        case "top-right":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width + deltaX)
                                                newHeight = newWidth / aspectRatio
                                                newY = startPosition.value.y + (startSize.value.height - newHeight)
                                                break
                                        case "top-left":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width - deltaX)
                                                newHeight = newWidth / aspectRatio
                                                newX = startPosition.value.x + deltaX
                                                newY = startPosition.value.y + (startSize.value.height - newHeight)
                                                break
                                        case "bottom-left":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width - deltaX)
                                                newHeight = newWidth / aspectRatio
                                                newX = startPosition.value.x + deltaX
                                                break
                                        case "bottom-right":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width + deltaX)
                                                newHeight = newWidth / aspectRatio
                                                break
                                }

                                // Scale fontSize proportionally for corner resizes (uniform scale)
                                const isCornerResize = ['top-right', 'top-left', 'bottom-left', 'bottom-right'].includes(resizeDirection.value)
                                if (isCornerResize) {
                                        const scaleFactor = newWidth / startSize.value.width
                                        fontSize.value = Math.max(12, startFontSize.value * scaleFactor)
                                }

                                width.value = newWidth
                                height.value = newHeight
                                x.value = newX
                                y.value = newY
                        }

                        const handleResizeUp = () => {
                                isResizing.value = false
                                resizeDirection.value = null
                                document.removeEventListener("mousemove", handleResizeMove)
                                document.removeEventListener("mouseup", handleResizeUp)
                                emit("update-size-position", id, {
                                        x: x.value,
                                        y: y.value,
                                        width: width.value,
                                        height: height.value,
                                        fontSize: fontSize.value
                                })
                        }

                        document.addEventListener("mousemove", handleResizeMove)
                        document.addEventListener("mouseup", handleResizeUp)
                }

                const startResizeTouch = (event, direction) => {
                        if (isLocked.value) return
                        if (!event.touches || event.touches.length === 0) return
                        
                        event.preventDefault()
                        event.stopPropagation()

                        closeAllMenus()
                        isResizing.value = true
                        resizeDirection.value = direction
                        
                        const touch = event.touches[0]
                        startCoords.value = { x: touch.clientX, y: touch.clientY }
                        startSize.value = { width: width.value, height: height.value }
                        startPosition.value = { x: x.value, y: y.value }
                        startFontSize.value = fontSize.value
                        const aspectRatio = width.value / height.value
                        const s = props.scale || 1

                        const handleResizeTouchMove = (e) => {
                                if (!isResizing.value) return
                                if (!e.touches || e.touches.length === 0) return
                                e.preventDefault()

                                const moveTouch = e.touches[0]
                                const deltaX = (moveTouch.clientX - startCoords.value.x) / s
                                const deltaY = (moveTouch.clientY - startCoords.value.y) / s

                                let newWidth = startSize.value.width
                                let newHeight = startSize.value.height
                                let newX = startPosition.value.x
                                let newY = startPosition.value.y

                                switch (resizeDirection.value) {
                                        case "right":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width + deltaX)
                                                break
                                        case "left":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width - deltaX)
                                                newX = startPosition.value.x + deltaX
                                                break
                                        case "bottom":
                                                newHeight = Math.max(MIN_SIZE, startSize.value.height + deltaY)
                                                break
                                        case "top":
                                                newHeight = Math.max(MIN_SIZE, startSize.value.height - deltaY)
                                                newY = startPosition.value.y + deltaY
                                                break
                                        case "top-right":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width + deltaX)
                                                newHeight = newWidth / aspectRatio
                                                newY = startPosition.value.y + (startSize.value.height - newHeight)
                                                break
                                        case "top-left":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width - deltaX)
                                                newHeight = newWidth / aspectRatio
                                                newX = startPosition.value.x + deltaX
                                                newY = startPosition.value.y + (startSize.value.height - newHeight)
                                                break
                                        case "bottom-left":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width - deltaX)
                                                newHeight = newWidth / aspectRatio
                                                newX = startPosition.value.x + deltaX
                                                break
                                        case "bottom-right":
                                                newWidth = Math.max(MIN_SIZE, startSize.value.width + deltaX)
                                                newHeight = newWidth / aspectRatio
                                                break
                                }

                                // Scale fontSize proportionally for corner resizes (uniform scale)
                                const isCornerResize = ['top-right', 'top-left', 'bottom-left', 'bottom-right'].includes(resizeDirection.value)
                                if (isCornerResize) {
                                        const scaleFactor = newWidth / startSize.value.width
                                        fontSize.value = Math.max(12, startFontSize.value * scaleFactor)
                                }

                                width.value = newWidth
                                height.value = newHeight
                                x.value = newX
                                y.value = newY
                        }

                        const handleResizeTouchEnd = () => {
                                isResizing.value = false
                                resizeDirection.value = null
                                document.removeEventListener("touchmove", handleResizeTouchMove)
                                document.removeEventListener("touchend", handleResizeTouchEnd)
                                emit("update-size-position", id, {
                                        x: x.value,
                                        y: y.value,
                                        width: width.value,
                                        height: height.value,
                                        fontSize: fontSize.value
                                })
                        }

                        document.addEventListener("touchmove", handleResizeTouchMove, { passive: false })
                        document.addEventListener("touchend", handleResizeTouchEnd)
                }

                const handleInput = (e) => {
                        text.value = e.target.innerText
                        emit("update:text", text.value)
                }

                const handleFocus = () => {
                        if (textContent.value?.innerText === t('legacy.Введите текст')) {
                                textContent.value.innerText = ""
                        }
                }

                const handleBlur = () => {
                        text.value = textContent.value?.innerText || ""
                        emit("update:text", text.value)
                        updatePlaceholder()
                        // Подстраиваем ширину под текст
                        nextTick(() => {
                                adjustWidthToContent()
                        })
                }

                const updatePlaceholder = () => {
                        if (!textContent.value) return
                        textContent.value.dataset.placeholder = t('legacy.Введите текст')
                        if (!text.value.trim()) {
                                textContent.value.innerText = ""
                        }
                }

                const updateColor = (e) => {
                        color.value = e.target.value
                        if (textContent.value) {
                                textContent.value.style.color = color.value
                        }

                        emit("update:color", id, color.value)
                }

                const updateColorFromPicker = (newColor) => {
                        color.value = newColor
                        if (textContent.value) {
                                textContent.value.style.color = newColor
                        }
                        emit("update:color", id, newColor)
                }

                const updateOpacity = (e) => {
                        opacity.value = parseInt(e.target.value) / 100
                        emit("update:opacity", opacity.value)
                }

                const toggleShadow = () => {
                        textShadow.value = !textShadow.value
                        emit("update:textShadow", id, textShadow.value)
                        closeAllMenus()
                }

                const toggleLock = () => {
                        isLocked.value = !isLocked.value
                        emit("lock-change", id, isLocked.value)
                        closeAllMenus()
                }

                const copyItem = () => {
                        const newItem = {
                                text: text.value,
                                x: x.value + 10,
                                y: y.value + 10,
                                width: width.value,
                                height: height.value,
                                rotate: rotate.value,
                                zIndex: zIndex.value,
                                color: color.value,
                                opacity: opacity.value,
                                locked: false,
                                fontName: fontName.value,
                                fontSize: fontSize.value,
                                id: Math.random().toString(36).slice(2),
                        }
                        emit("copy-item", newItem)
                        closeAllMenus()
                }

                const deleteItem = () => {
                        emit("delete-item", id)
                }

                const moveLayer = (direction) => {
                        emit("move-layer", id, direction)
                }

                const autoResizeWidth = () => {
                        if (!textContent.value) return

                        const el = textContent.value
                        el.style.whiteSpace = "nowrap"
                        el.style.width = "auto"

                        const scrollWidth = el.scrollWidth
                        if (scrollWidth > width.value) {
                                width.value = scrollWidth + 16
                        }

                        el.style.whiteSpace = ""
                        el.style.width = "100%"

                        nextTick(() => {
                                adjustSizeForRotation()
                        })
                }

                const handleKeyDown = (e) => {
                        if ((e.key === "Delete" || e.key === "Backspace") && !isLocked.value) {
                                const selection = window.getSelection()
                                if (selection && selection.toString().trim() === "" && !text.value.trim()) {
                                        deleteItem()
                                }
                        }
                }

                const handleDocumentClick = (e) => {
                        if (!textContent.value?.contains(e.target)) {
                                closeAllMenus()
                        }
                }

                onMounted(() => {
                        if (textContent.value) {
                                if (!text.value) {
                                        updatePlaceholder()
                                } else {
                                        textContent.value.innerText = text.value
                                }
                                textContent.value.addEventListener("keydown", handleKeyDown)
                                adjustSizeForRotation()
                        }

                        document.addEventListener('click', handleDocumentClick)
                })

                onUnmounted(() => {
                        document.removeEventListener('click', handleDocumentClick)
                })

                watch(() => props.item.text, (newVal) => {
                        text.value = newVal
                        updatePlaceholder()
                })

                watch(() => props.item.fontName, (newVal) => {
                        if (newVal) {
                                fontName.value = newVal
                        }
                })

                watch(() => props.item.fontCategory, (newVal) => {
                        if (newVal) {
                                fontCategory.value = newVal
                        }
                })

                watch(() => props.item.textShadow, (newVal) => {
                        textShadow.value = newVal || false
                })

                watch(() => props.item.color, (newVal) => {
                        if (newVal) {
                                color.value = newVal
                        }
                })

                watch(() => props.item.fontSize, (newVal) => {
                        if (newVal) {
                                fontSize.value = newVal
                        }
                })

                watch(() => props.item.zIndex, (newVal) => {
                        if (newVal !== undefined) {
                                zIndex.value = newVal
                        }
                })

                watch(() => props.item.rotate, (newVal) => {
                        if (newVal !== undefined) {
                                rotate.value = newVal
                        }
                })

                watch(() => props.item.opacity, (newVal) => {
                        if (newVal !== undefined) {
                                opacity.value = newVal
                        }
                })

                return {
                        id,
                        x,
                        y,
                        width,
                        height,
                        rotate,
                        zIndex,
                        text,
                        fontName,
                        fontCategory,
                        fontSize,
                        color,
                        opacity,
                        textShadow,
                        isLocked,
                        showRotatePicker,
                        textContent,
                        containerStyle,
                        textStyle,
                        activeControlButton,
                        handleControlButtonClick,
                        closeAllMenus,
                        handleMouseDown,
                        handleTouchStart,
                        handleInput,
                        handleFocus,
                        handleBlur,
                        updateColor,
                        updateColorFromPicker,
                        updateOpacity,
                        toggleShadow,
                        toggleLock,
                        copyItem,
                        deleteItem,
                        moveLayer,
                        startResize,
                        startResizeTouch,
                        resizeHandles,
                        sliderStyle,
                        toolbarBelow,
                        autoResizeWidth,
                        openRotatePicker,
                        closeRotatePicker,
                        handleRotateUpdate
                }
        },
}
</script>

<style scoped>
.settings-inputs {
        position: absolute;
        background: #fff;

        box-shadow: 0 2px 4px rgba(128, 117, 116, .05),
                0 4px 8px rgba(128, 117, 116, .08),
                0 8px 12px rgba(128, 117, 116, .1);
        border-radius: 5px;
        display: none;
        gap: 10px;
        flex-direction: column;
        align-items: stretch;
        padding: 10px;
        min-width: 220px;
}

.text-content[data-placeholder]:empty::before {
        content: attr(data-placeholder);
        color: #999;
        direction: rtl;
}

.settings-block:hover .settings-inputs {
        display: flex;
}

.settings-inputs-block {
        display: grid;
        grid-template-columns: 110px 150px 40px;
        align-items: center;
        gap: 10px;
}

.settings-text {
        color: #26374e;
        font-size: 14px;
        font-weight: 600;
        line-height: 120%;
        white-space: nowrap;
        text-align: left;
}

.settings-number {
        color: #26374e;
        font-size: 14px;
        font-weight: 600;
        line-height: 120%;
        white-space: nowrap;
        text-align: right;
}

.settings-input {
        width: 150px;
        height: 4px;
        -webkit-appearance: none;
        appearance: none;
        margin: 0;
        cursor: pointer;
        background: linear-gradient(to right,
                        #e0e4e8 0%,
                        #e0e4e8 var(--left-fill, 50%),
                        #496782 var(--left-fill, 50%),
                        #496782 var(--right-fill, 50%),
                        #e0e4e8 var(--right-fill, 50%),
                        #e0e4e8 100%);
        border-radius: 10px;
        outline: none;
}

.settings-input::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #496782;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.settings-input::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #496782;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}


.text-element-container {
        width: 100%;
        height: 100%;
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        background: rgba(200, 200, 255, 0.3);
        pointer-events: auto;
        min-width: 50px;
        min-height: 30px;
}

.text-content[contenteditable="true"] {
        outline: none;
        -webkit-user-modify: read-write-plaintext-only;
        user-select: text;
}

.text-content:focus {
        outline: none;
        border: none;
}

.text-element-container.active {
        border: 1px solid #bbcad8 !important;

        border-radius: 5px !important;
}

.text-element-container.locked {
        cursor: default;
        outline-color: #ccc;
}

.text-content {
        width: 100%;
        height: 100%;
        padding: 8px;
        transition: 0.4s ease;
        box-sizing: border-box;
}

.text-content {
        width: 100%;
        height: 100%;
        padding: 8px;
        box-sizing: border-box;
        transition: 0.s ease;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        outline: none;
        user-select: text;
}

/* Панель инструментов (как у элементов) */
.images-buttons {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        top: -45px;
        left: 50%;
        border-radius: 5px;
        transform: translateX(-50%);
        padding: 5px 10px;
        height: 34px;
        cursor: pointer;
        background: #fff;
        z-index: 1001;
        box-shadow: 0 2px 4px rgba(128, 117, 116, .05),
                0 4px 8px rgba(128, 117, 116, .08),
                0 8px 12px rgba(128, 117, 116, .1);
        white-space: nowrap;
}

.images-buttons.toolbar-below {
        top: calc(100% + 60px);
        bottom: auto;
}

.images-buttons img {
        width: 20px;
        height: 20px;
        cursor: pointer;
        transition: transform 0.2s ease;
}

.images-buttons img:hover {
        transform: scale(1.1);
}

.images-buttons img.active {
        filter: invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%);
}

.images-buttons .rotate-icon,
.images-buttons .delete-icon,
.images-buttons .shadow-icon {
        width: 24px;
        height: 24px;
}

.images-buttons .shadow-icon.active {
        background-color: rgba(38, 55, 78, 0.1);
        border-radius: 4px;
}

/* Ресайз-ручки */
.resize-handle {
        position: absolute;
        background-color: #bbcad8;
        opacity: 1 !important;
        z-index: 10;
}

.resize-handle-left,
.resize-handle-right {
        width: 4px;
        height: 30px;
        top: 50%;
        transform: translateY(-50%);
        cursor: ew-resize;
}

.resize-handle-left {
        left: -2px;
}

.resize-handle-right {
        right: -2px;
}

.resize-handle-top,
.resize-handle-bottom {
        width: 30px;
        height: 4px;
        left: 50%;
        transform: translateX(-50%);
        cursor: ns-resize;
        z-index: 10;
}

.resize-handle-top {
        top: -2px;
}

.resize-handle-bottom {
        bottom: -2px;
}

.resize-handle-top-left,
.resize-handle-top-right,
.resize-handle-bottom-left,
.resize-handle-bottom-right {
        width: 12px;
        height: 12px;
        background-color: #bbcad8;
        border-radius: 2px;
        z-index: 10;
}

.resize-handle-top-left {
        top: -6px;
        left: -6px;
        cursor: nwse-resize;
}

.resize-handle-top-right {
        top: -6px;
        right: -6px;
        cursor: nesw-resize;
}

.resize-handle-bottom-left {
        bottom: -6px;
        left: -6px;
        cursor: nesw-resize;
}

.resize-handle-bottom-right {
        bottom: -6px;
        right: -6px;
        cursor: nwse-resize;
}

/* Элементы управления */
.index-block-input {
        position: absolute;
        background: #fff;
        box-shadow: 0 2px 4px rgba(128, 117, 116, .05),
                0 4px 8px rgba(128, 117, 116, .08),
                0 8px 12px rgba(128, 117, 116, .1);
        border-radius: 5px;
        display: none;
        padding: 10px;
        min-width: 220px;
        z-index: 1000;
}

.indexIcon.is-active .index-block-input {
        display: block;
}

.index-inputs-block {
        display: grid;
        grid-template-columns: 110px 150px 40px;
        align-items: center;
        gap: 10px;
}

.index-text {
        color: #26374e;
        font-size: 14px;
        font-weight: 600;
        line-height: 120%;
        white-space: nowrap;
        text-align: left;
}

.index-number {
        color: #26374e;
        font-size: 14px;
        font-weight: 600;
        line-height: 120%;
        white-space: nowrap;
        text-align: right;
}

.index-input {
        width: 150px;
        height: 4px;
        -webkit-appearance: none;
        appearance: none;
        margin: 0;
        cursor: pointer;
        background: linear-gradient(to right,
                        #496782 0%,
                        #496782 var(--fill-position, 100%),
                        #e0e4e8 var(--fill-position, 100%),
                        #e0e4e8 100%);
        border-radius: 10px;
        outline: none;
}

.index-input::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #496782;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.layers-menu {
        position: absolute;
        background: #fff;
        box-shadow: 0 2px 4px rgba(128, 117, 116, .05),
                0 4px 8px rgba(128, 117, 116, .08),
                0 8px 12px rgba(128, 117, 116, .1);
        border-radius: 5px;
        display: none;
        padding: 10px;
        min-width: 180px;
        z-index: 1000;
}

.layers-control.is-active .layers-menu {
        display: block;
}

.layers-menu div {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        cursor: pointer;
        white-space: nowrap;
}

.layers-menu div:hover {
        background-color: #f5f5f5;
}

.layers-menu img {
        width: 16px;
        height: 16px;
}

/* Цветовой пикер */
/* Адаптивные правки */
.item-controls {
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        z-index: 1001;
}

.item-controls.shift-right {
        left: 100%;
        margin-left: 10px;
}

.color-picker-wrapper {
        position: relative;
        display: inline-block;
}

.color-preview-btn {
        width: 24px;
        height: 24px;
        border-radius: 4px;
        border: 2px solid #e0e3e7;
        cursor: pointer;
        transition: transform 0.2s;
}

.color-preview-btn:hover {
        transform: scale(1.1);
}

.color-picker-dropdown {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 8px;
        z-index: 1002;
        display: none;
}

.color-picker-wrapper.is-active .color-picker-dropdown {
        display: block;
}

/* Скрываем старую плавающую панель на десктопе - используем новый сайдбар */
@media (min-width: 1025px) {
        .item-controls {
                display: none !important;
        }
}
</style>